/**
 * Audit Log Panel - Shows project activity history
 */

import { useState } from "react"
import { useAuditLogs, getActionLabel, getResourceLabel, type AuditLogOptions } from "@/hooks/use-audit-logs"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ChevronLeft, ChevronRight, Clock, User, FileText, ChevronDown, ChevronUp } from "lucide-react"
import { formatDistanceToNow } from "date-fns"

interface AuditLogPanelProps {
  projectId: string
  projectName: string
}

// Common action types for filtering
const ACTION_FILTERS = [
  { value: "all", label: "All Actions" },
  { value: "CREATE", label: "Created" },
  { value: "UPDATE", label: "Updated" },
  { value: "DELETE", label: "Deleted" },
  { value: "ArtefactCreated", label: "Artifact Created" },
  { value: "ArtefactUpdated", label: "Artifact Updated" },
  { value: "ArtefactStatusChanged", label: "Status Changed" },
  { value: "MemberAdded", label: "Member Added" },
  { value: "MemberRemoved", label: "Member Removed" },
  { value: "FilesUploaded", label: "Files Uploaded" },
  { value: "DocumentDeleted", label: "Document Deleted" },
]

// Common resource types for filtering
const RESOURCE_FILTERS = [
  { value: "all", label: "All Resources" },
  { value: "project", label: "Project" },
  { value: "document", label: "Documents" },
  { value: "definition_persona", label: "Personas" },
  { value: "definition_use_case", label: "Use Cases" },
  { value: "definition_journey", label: "Journeys" },
  { value: "definition_service_map", label: "Service Maps" },
  { value: "definition_storyboard", label: "Storyboards" },
  { value: "goal", label: "Goals" },
  { value: "story", label: "Stories" },
]

export function AuditLogPanel({ projectId, projectName }: AuditLogPanelProps) {
  const [page, setPage] = useState(1)
  const [actionFilter, setActionFilter] = useState("all")
  const [resourceFilter, setResourceFilter] = useState("all")
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const pageSize = 20

  const options: AuditLogOptions = {
    page,
    pageSize,
    ...(actionFilter !== "all" && { action: actionFilter }),
    ...(resourceFilter !== "all" && { resource: resourceFilter }),
  }

  const { data, isLoading, error } = useAuditLogs(projectId, options)

  const toggleRowExpanded = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleFilterChange = () => {
    setPage(1)
  }

  if (error) {
    return (
      <div className="flex-1 bg-gray-50 flex items-center justify-center p-6">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to Load Audit Log</h3>
          <p className="text-gray-600">
            {error instanceof Error ? error.message : "You may not have permission to view the audit log."}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 bg-gray-50 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Audit Log</h2>
            <p className="text-sm text-gray-500">Activity history for {projectName}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4">
          <Select
            value={actionFilter}
            onValueChange={(value) => {
              setActionFilter(value)
              handleFilterChange()
            }}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by action" />
            </SelectTrigger>
            <SelectContent>
              {ACTION_FILTERS.map((filter) => (
                <SelectItem key={filter.value} value={filter.value}>
                  {filter.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={resourceFilter}
            onValueChange={(value) => {
              setResourceFilter(value)
              handleFilterChange()
            }}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by resource" />
            </SelectTrigger>
            <SelectContent>
              {RESOURCE_FILTERS.map((filter) => (
                <SelectItem key={filter.value} value={filter.value}>
                  {filter.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-4">
                  <Skeleton className="w-10 h-10 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="w-48 h-4 mb-2" />
                    <Skeleton className="w-32 h-3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : data?.logs.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Activity Yet</h3>
            <p className="text-gray-600">
              Activity in this project will appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {data?.logs.map((log) => (
              <div
                key={log.id}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden"
              >
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleRowExpanded(log.id)}
                >
                  <div className="flex items-start gap-4">
                    {/* User Avatar */}
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                      {log.user?.avatarUrl ? (
                        <img
                          src={log.user.avatarUrl}
                          alt={log.user.name || "User"}
                          className="w-10 h-10 rounded-full"
                        />
                      ) : (
                        <User className="w-5 h-5 text-gray-500" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900">
                          {log.user?.name || log.user?.email || "System"}
                        </span>
                        <span className="text-gray-600">
                          {getActionLabel(log.action)}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          <FileText className="w-3 h-3 mr-1" />
                          {getResourceLabel(log.resource)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}
                        {log.resourceId && (
                          <span className="text-xs text-gray-400">
                            ID: {log.resourceId.slice(0, 8)}...
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Expand/Collapse */}
                    {(log.oldValue || log.newValue) && (
                      <div className="flex-shrink-0">
                        {expandedRows.has(log.id) ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedRows.has(log.id) && (log.oldValue || log.newValue) && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <div className="grid grid-cols-2 gap-4">
                      {log.oldValue && (
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">
                            Previous Value
                          </h4>
                          <pre className="text-xs bg-white p-3 rounded border border-gray-200 overflow-auto max-h-40">
                            {JSON.stringify(log.oldValue, null, 2)}
                          </pre>
                        </div>
                      )}
                      {log.newValue && (
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">
                            New Value
                          </h4>
                          <pre className="text-xs bg-white p-3 rounded border border-gray-200 overflow-auto max-h-40">
                            {JSON.stringify(log.newValue, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <div className="bg-white border-t border-gray-200 px-6 py-3 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, data.total)} of {data.total} entries
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>
            <span className="text-sm text-gray-600">
              Page {page} of {data.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page === data.totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
